import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '../../context/I18nContext';
import { Payment, Project } from '../../types';
import BaseFormModal from '../ui/BaseFormModal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import ProjectSearch from './ProjectSearch';
import type { RequestCreateProjectArgs } from '../../hooks/useCreateProjectFromSearch';

interface PaymentFormData {
  projectId: string;
  issueDate: string;
  issueDateObj?: Date | null;
  dueDate: string;
  dueDateObj?: Date | null;
  paymentDate?: string;
  paymentDateObj?: Date | null;
  amount: number;
  currency: string;
  amountBaseCurrency: number; // Amount in EUR for statistics
  status: 'sent' | 'paid' | 'overdue';
}

const formatLocalDateInput = (date: Date): string => {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const parseLocalDateInput = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  const dt = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payment: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  payment?: Payment | null;
  projects: Project[];
  leads: any[];
  searchProjectsFn?: (term: string) => Promise<Project[]>;
  preselectedProject?: Project | null;
  title?: string;
  onCreateProject?: (args: RequestCreateProjectArgs) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  payment,
  projects,
  leads,
  searchProjectsFn,
  preselectedProject,
  title,
  onCreateProject,
}) => {
  const { t } = useI18n();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  /** Avoid create-mode re-init when `projects` updates after inline create. */
  const createInitializedRef = useRef(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<PaymentFormData>();

  useEffect(() => {
    if (!isOpen) {
      createInitializedRef.current = false;
      return;
    }

    if (payment) {
      // Edit mode — keep in sync when projects list resolves
      const project = projects.find(p => p.id === payment.projectId);
      setSelectedProject(project || null);
      setValue('projectId', payment.projectId);
      const issueDateObj = new Date(payment.issueDate);
      const dueDateObj = new Date(payment.dueDate);
      setValue('issueDateObj', issueDateObj);
      setValue('issueDate', formatLocalDateInput(issueDateObj), { shouldValidate: true, shouldDirty: true });
      setValue('dueDateObj', dueDateObj);
      setValue('dueDate', formatLocalDateInput(dueDateObj), { shouldValidate: true, shouldDirty: true });
      setValue('amount', payment.amount);
      setValue('currency', payment.currency || project?.currency || 'EUR');
      setValue('amountBaseCurrency', payment.amountBaseCurrency || (payment.currency === 'EUR' ? payment.amount : 0));
      setValue('status', payment.status);
      if (payment.paymentDate) {
        const paymentDateObj = new Date(payment.paymentDate);
        setValue('paymentDate', formatLocalDateInput(paymentDateObj));
        setValue('paymentDateObj', paymentDateObj);
      }
      return;
    }

    // Create mode: init once per open (do not reset when projects/leads grow after inline create)
    if (createInitializedRef.current) {
      // Deep link: project fetched after modal opened — apply when it arrives
      if (preselectedProject && !getValues('projectId')) {
        setSelectedProject(preselectedProject);
        setValue('projectId', preselectedProject.id, { shouldValidate: true });
        setValue('amount', preselectedProject.amount || 0);
        setValue('currency', preselectedProject.currency || 'EUR');
      }
      return;
    }
    createInitializedRef.current = true;

    if (preselectedProject) {
      setSelectedProject(preselectedProject);
      setValue('projectId', preselectedProject.id);
      setValue('amount', preselectedProject.amount || 0);
      setValue('currency', preselectedProject.currency || 'EUR');
    } else {
      setSelectedProject(null);
    }
    const initialCurrency = preselectedProject?.currency || 'EUR';
    const initialAmount = preselectedProject?.amount || 0;
    reset({
      projectId: preselectedProject?.id || '',
      issueDate: formatLocalDateInput(new Date()),
      dueDate: formatLocalDateInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days from now
      paymentDate: '',
      amount: initialAmount,
      currency: initialCurrency,
      status: 'sent',
    });
  }, [isOpen, payment, projects, preselectedProject, setValue, reset, getValues]);

  const handleProjectSelect = (project: Project | null) => {
    setSelectedProject(project);
    if (project) {
      setValue('projectId', project.id, { shouldValidate: true, shouldDirty: true });
      setValue('amount', project.amount || 0); // Auto-fill amount from project
      const projectCurrency = project.currency || 'EUR';
      setValue('currency', projectCurrency); // Auto-fill currency from project
      // amountBaseCurrency will be set when payment is marked as paid
    } else {
      setValue('projectId', '', { shouldValidate: true, shouldDirty: true });
      setValue('amount', 0); // Reset amount when no project selected
      setValue('currency', 'EUR'); // Reset currency to default
      // amountBaseCurrency will be set when payment is marked as paid
    }
  };

  // Watch status changes and set payment date when status changes to 'paid'
  const currentStatus = watch('status');
  const currentPaymentDate = watch('paymentDate');
  
  useEffect(() => {
    if (currentStatus === 'paid' && !currentPaymentDate) {
      // Auto-fill payment date with today's date when status changes to 'paid'
      const today = new Date();
      setValue('paymentDate', formatLocalDateInput(today));
      setValue('paymentDateObj', today);
    }
  }, [currentStatus, currentPaymentDate, setValue]);

  const onSubmit = async (data: PaymentFormData) => {
    if (!data.projectId) return;
    
    const issueDate = data.issueDateObj || parseLocalDateInput(data.issueDate);
    const dueDate = data.dueDateObj || parseLocalDateInput(data.dueDate);
    if (!issueDate || !dueDate) return;

    const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
    const currency = data.currency || 'EUR';
    
    // amountBaseCurrency logic:
    // - Base currency: always mirror amount (optimistic chart uses amountBaseCurrency when set)
    // - Non-base + paid: use provided amountBaseCurrency
    // - Non-base + unpaid: preserve existing or 0 until marked paid
    const amountBaseCurrency =
      currency === baseCurrency
        ? Number(data.amount)
        : data.status === 'paid' && data.amountBaseCurrency != null
          ? Number(data.amountBaseCurrency)
          : Number(payment?.amountBaseCurrency || 0);

    const paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'> = {
      projectId: data.projectId,
      issueDate,
      dueDate,
      amount: data.amount,
      currency: currency,
      amountBaseCurrency: amountBaseCurrency,
      status: data.status,
      // When marking as paid, always set payment date (required for revenue summary by month)
      paymentDate: data.status === 'paid'
        ? (data.paymentDateObj || (data.paymentDate ? parseLocalDateInput(data.paymentDate) : null) || new Date())
        : undefined,
    };

    // Close modal immediately for snappy UX
    onClose();
    
    // Save in background
    Promise.resolve(onSave(paymentData)).catch(error => {
      console.error('Error saving payment:', error);
    });
  };

  const statusOptions = [
    { value: 'sent', label: t('payments.statusLabels.sent') },
    { value: 'paid', label: t('payments.statusLabels.paid') },
    { value: 'overdue', label: t('payments.statusLabels.overdue') },
  ];

  const currencyOptions = [
    { value: 'EUR', label: '€ Euro' },
    { value: 'USD', label: '$ Dollar' },
    { value: 'GBP', label: '£ Livre' },
  ];

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={onClose}
      resourceKey="payments"
      size="lg"
      isLoading={false}
      mode={payment ? 'update' : 'create'}
      onValidate={handleSubmit(onSubmit)}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Register non-input fields for validation */}
        <input type="hidden" {...register('projectId', { required: t('payments.projectRequired') as any })} />
        <input type="hidden" {...register('issueDate', { required: t('payments.issueDateRequired') as any })} />
        <input type="hidden" {...register('dueDate', { required: t('payments.dueDateRequired') as any })} />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('payments.project')}
          </label>
          <ProjectSearch
            projects={projects}
            leads={leads}
            searchFn={searchProjectsFn}
            selectedProject={selectedProject}
            onSelectProject={handleProjectSelect}
            placeholder={t('projects.searchProject')}
            error={errors.projectId?.message}
            onCreateProject={payment ? undefined : onCreateProject}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DatePicker
            label={t('payments.issueDate')}
            value={watch('issueDateObj') || (watch('issueDate') && watch('issueDate') !== '' ? new Date(watch('issueDate')!) : null)}
            onChange={(date) => {
              setValue('issueDateObj', date);
              setValue('issueDate', date ? formatLocalDateInput(date) : '', { shouldValidate: true, shouldDirty: true });
            }}
            error={errors.issueDate?.message}
            allowClear={false}
          />

          <DatePicker
            label={t('payments.dueDate')}
            value={watch('dueDateObj') || (watch('dueDate') && watch('dueDate') !== '' ? new Date(watch('dueDate')!) : null)}
            onChange={(date) => {
              setValue('dueDateObj', date);
              setValue('dueDate', date ? formatLocalDateInput(date) : '', { shouldValidate: true, shouldDirty: true });
            }}
            error={errors.dueDate?.message}
            allowClear={false}
          />
        </div>

        {watch('status') === 'paid' && (
          <DatePicker
            label={t('payments.paymentDate')}
            value={watch('paymentDateObj') || (watch('paymentDate') && watch('paymentDate') !== '' ? new Date(watch('paymentDate')!) : null)}
            onChange={(date) => {
              setValue('paymentDateObj', date);
              setValue('paymentDate', date ? formatLocalDateInput(date) : '');
            }}
            error={errors.paymentDate?.message}
            allowClear={false}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label={t('payments.amount')}
            type="number"
            step="0.01"
            min="0"
            {...register('amount', {
              required: t('payments.amountRequired') as any,
              min: { value: 0.01, message: t('payments.amountMin') as any },
            })}
            error={errors.amount?.message}
            placeholder="0.00"
          />

          <Select
            label={t('projects.currency')}
            value={watch('currency') || 'EUR'}
            onChange={(value) => setValue('currency', value)}
            options={currencyOptions}
          />

          <Select
            label={t('payments.status')}
            value={watch('status') || ''}
            onChange={(value) => setValue('status', value as 'sent' | 'paid' | 'overdue')}
            options={statusOptions}
          />
        </div>

        {payment && watch('status') === 'paid' && watch('currency') !== 'EUR' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <Input
              label={t('payments.amountReceivedBaseCurrency', { currency: 'EUR' })}
              type="number"
              step="0.01"
              min="0"
              {...register('amountBaseCurrency', {
                required: t('payments.amountBaseCurrencyRequired') as any,
                min: { value: 0.01, message: t('payments.amountMin') as any },
              })}
              error={errors.amountBaseCurrency?.message}
              placeholder="0.00"
            />
            <p className="text-sm text-gray-500 pb-2">
              {t('payments.amountReceivedBaseCurrencyHelp')}
            </p>
          </div>
        )}
      </form>
    </BaseFormModal>
  );
};

export default PaymentModal;
